import { jwtDecode } from 'jwt-decode';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input, message } from 'antd';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SignInContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #181A20 0%,
    #1a1d26 25%,
    #0f1115 50%,
    #1a1d26 75%,
    #181A20 100%
  );
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(14, 203, 129, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(14, 203, 129, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const SignInCard = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background-color: #1E2329;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid #2B3139;
  position: relative;
  z-index: 1;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  
  img {
    height: 60px;
    width: auto;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 24px;
  margin-bottom: 20px;
  color: #EAECEF;
`;

const SignUpText = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #848E9C;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SocialButton = styled(Button)`
  width: 100%;
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  background-color: #2B3139;
  border: 1px solid #474D57;
  color: #EAECEF;

  &:hover {
    background-color: #474D57 !important;
    border-color: ${({ theme }) => theme.colors.primary} !important;
    color: #EAECEF !important;
  }
`;

const OrDivider = styled.div`
  text-align: center;
  margin: 16px 0;
  color: #848E9C;
`;

const ForgotPassword = styled.div`
  text-align: right;
  margin: 8px 0 16px;
  font-size: 14px;
  
  a {
    color: ${({ theme }) => theme.colors.text.secondary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignInButton = styled(Button)`
  width: 100%;
  height: 40px;
  background-color: #0ECB81 !important;
  border: none !important;
  font-weight: 600;
  color: #0B0E11 !important;
  transition: opacity 0.3s ease;

  &:hover {
    background-color: #0ECB81 !important;
    opacity: 0.8 !important;
  }

  &:focus {
    background-color: #0ECB81 !important;
    opacity: 1 !important;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #EAECEF;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorText = styled.div`
  color: #F6465D;
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
`;

const FooterText = styled.div`
  margin-top: 20px;
  font-size: 14px;
  color: #848E9C;
  text-align: center;
  position: relative;
  z-index: 1;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SSOLink = styled.div`
  text-align: center;
  margin-top: 20px;
  
  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;
    font-size: 14px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledInput = styled(Input)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;
  color: #EAECEF !important;

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  .ant-input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  .ant-input-suffix {
    color: #848E9C !important;
  }
`;

const coreService = apiService.getService(ServiceType.CORE)
interface DecodedToken {
  sub?: string;
  user_id?: string;
  id?: string;
  [key: string]: any;
}
const SignInPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Error states
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Validation functions
  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (value: string) => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const checkUserPortfolios = async (token: string) => {
    try {
      const response = await coreService.get('/portfolios', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check if user has any portfolios
      const portfolios = response.data;
      return Array.isArray(portfolios) && portfolios.length > 0;
    } catch (error) {
      console.error('Error checking portfolios:', error);
      return false;
    }
  };

  const handleSignIn = async () => {
    // Validate all fields
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);

    try {
      const response = await coreService.post('/auth/login', {
        email,
        password
      });

      // Extract tokens and user data from response
      const { accessToken, refreshToken } = response.data;
      const decoded = jwtDecode<DecodedToken>(accessToken);
      const userId = decoded.user_id || decoded.sub || decoded.id;
      
      // Store tokens and user data in localStorage 
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('user_id', userId || '');

      // Update auth context
      login();

      message.success('Sign in successful!');

      // Check if user has portfolios
      const hasPortfolios = await checkUserPortfolios(accessToken);

      if (!hasPortfolios) {
        // Redirect to register portfolio if no portfolios found
        navigate('/register-portfolio');
      } else {
        // Redirect to home page if user has portfolios
        navigate('/home');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      message.error('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignInContainer>
      <SignInCard>
        <Logo>
          <img src="/Algotrade - White.png" alt="Algotrade" />
        </Logo>
        <Title>Sign In</Title>

        <div>
          <div style={{ marginBottom: '8px' }}>
            <Label>Email</Label>
            <StyledInput
              placeholder="Email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={(e) => validateEmail(e.target.value)}
              onPressEnter={handleSignIn}
              size="large"
              status={emailError ? 'error' : ''}
            />
            <ErrorText>{emailError}</ErrorText>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <Label>Password</Label>
            <StyledPasswordInput
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) validatePassword(e.target.value);
              }}
              onBlur={(e) => validatePassword(e.target.value)}
              onPressEnter={handleSignIn}
              size="large"
              status={passwordError ? 'error' : ''}
            />
            <ErrorText>{passwordError}</ErrorText>
          </div>

          <ForgotPassword>
            <Link to="/forgot-password">Reset password?</Link>
          </ForgotPassword>

          <SignInButton  onClick={handleSignIn} loading={loading}>
            SIGN IN
          </SignInButton>
        </div>

        <SignUpText>
          Not registered yet? <Link to="/signup">Sign Up</Link>
        </SignUpText>

        <SSOLink>
          <Link to="/single-sign-on">Use single sign-on</Link>
        </SSOLink>
      </SignInCard>

      <FooterText>
        By continuing, you agree to Algotrade's <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
      </FooterText>
    </SignInContainer>
  );
};

export default SignInPage;